import { useCallback } from "react";
import { UpdateClassesChange } from "@/store/zustand/changeLedgerTypes";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { TempoElement } from "@/utils/tempoElement";
import {
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "../stores/canvasStore";

type ClassPattern = {
  startsWith?: string[];
  includes?: string[];
};

type ToggleConfig = {
  pattern: ClassPattern;
  activeClass: string;
  inactiveClass: string;
  isActive: (classes: string[]) => boolean;
};

type SetConfig = {
  pattern: ClassPattern;
  className: string;
};

const matchesPattern = (cls: string, pattern: ClassPattern): boolean => {
  if (pattern.startsWith?.some((p) => cls.startsWith(p))) return true;
  if (pattern.includes?.some((p) => cls.includes(p))) return true;
  return false;
};

const TOGGLE_CONFIGS: Record<string, ToggleConfig> = {
  bold: {
    pattern: { startsWith: ["font-"] },
    activeClass: "font-bold",
    inactiveClass: "font-normal",
    isActive: (classes) =>
      classes.some((c) => c.startsWith("font-") && c !== "font-normal"),
  },
  italic: {
    pattern: { includes: ["italic", "not-italic"] },
    activeClass: "italic",
    inactiveClass: "not-italic",
    isActive: (classes) => classes.includes("italic"),
  },
  underline: {
    pattern: { includes: ["underline", "no-underline"] },
    activeClass: "underline",
    inactiveClass: "no-underline",
    isActive: (classes) => classes.includes("underline"),
  },
  strikethrough: {
    pattern: { includes: ["line-through", "no-underline"] },
    activeClass: "line-through",
    inactiveClass: "no-underline",
    isActive: (classes) => classes.includes("line-through"),
  },
};

const TEXT_ALIGN_CLASSES = [
  "text-left",
  "text-center",
  "text-right",
  "text-justify",
];

const SET_CONFIGS: Record<string, SetConfig> = {
  alignLeft: {
    pattern: { includes: TEXT_ALIGN_CLASSES },
    className: "text-left",
  },
  alignCenter: {
    pattern: { includes: TEXT_ALIGN_CLASSES },
    className: "text-center",
  },
  alignRight: {
    pattern: { includes: TEXT_ALIGN_CLASSES },
    className: "text-right",
  },
  alignJustify: {
    pattern: { includes: TEXT_ALIGN_CLASSES },
    className: "text-justify",
  },
};

const AUTO_LAYOUT_PATTERNS: ClassPattern = {
  includes: ["flex", "items-", "justify-", "gap-", "self-", "grow", "shrink"],
};

const FILL_PATTERNS: ClassPattern = {
  startsWith: ["bg-", "from-", "via-", "to-"],
  includes: ["background", "gradient"],
};

const STROKE_PATTERNS: ClassPattern = {
  startsWith: ["border", "outline", "ring-", "decoration-"],
  includes: ["stroke"],
};

export const useDesignPropertyShortcuts = () => {
  const { selectedElement } = useSelectedNavTreeNode();
  const { multiselectedElements } = useMultiselectedNavTreeNode();

  const getCurrentSelectedElements = useCallback(() => {
    const allSelectedElements = [
      ...(selectedElement && !selectedElement.isEmpty()
        ? [selectedElement]
        : []),
      ...multiselectedElements,
    ];

    return allSelectedElements.filter(
      (element) => !element.isEmpty() && element.isKnownElement(),
    );
  }, [selectedElement, multiselectedElements]);

  const applyClassToggle = useCallback(
    (config: ToggleConfig) => {
      const elements = getCurrentSelectedElements();
      if (elements.length === 0) return;

      for (const element of elements) {
        const treeElement =
          useCodebaseStore.getState().treeElementLookup[element.codebaseId];
        if (!treeElement) continue;

        const elementClasses = treeElement.removableClasses || [];
        const filteredClasses = elementClasses.filter(
          (cls) => !matchesPattern(cls, config.pattern),
        );

        const isCurrentlyActive = config.isActive(elementClasses);
        let newClasses: string[];

        if (isCurrentlyActive) {
          const hasOtherDecorations = filteredClasses.some((cls) =>
            ["underline", "line-through", "overline"].includes(cls),
          );
          newClasses = hasOtherDecorations
            ? filteredClasses
            : [...filteredClasses, config.inactiveClass];
        } else {
          newClasses = [...filteredClasses, config.activeClass];
        }

        const change = new UpdateClassesChange({
          codebaseIdToUpdateClass: element.codebaseId,
          oldClasses: elementClasses,
          newClasses,
          involvedClasses: [],
          isInstantChangeOnly: false,
        });

        useDesignCommandManager.getState().executeCommand(change);
      }
    },
    [getCurrentSelectedElements],
  );

  const applyClassSet = useCallback(
    (config: SetConfig) => {
      const elements = getCurrentSelectedElements();
      if (elements.length === 0) return;

      for (const element of elements) {
        const treeElement =
          useCodebaseStore.getState().treeElementLookup[element.codebaseId];
        if (!treeElement) continue;

        const elementClasses = treeElement.removableClasses || [];
        const filteredClasses = elementClasses.filter(
          (cls) => !matchesPattern(cls, config.pattern),
        );

        const change = new UpdateClassesChange({
          codebaseIdToUpdateClass: element.codebaseId,
          oldClasses: elementClasses,
          newClasses: [...filteredClasses, config.className],
          involvedClasses: [],
          isInstantChangeOnly: false,
        });

        useDesignCommandManager.getState().executeCommand(change);
      }
    },
    [getCurrentSelectedElements],
  );

  const applyClassChanges = useCallback(
    (filterFn: (cls: string) => boolean, addClasses: string[] = []) => {
      const elements = getCurrentSelectedElements();
      if (elements.length === 0) return;

      for (const element of elements) {
        const treeElement =
          useCodebaseStore.getState().treeElementLookup[element.codebaseId];
        if (!treeElement) continue;

        const elementClasses = treeElement.removableClasses || [];
        const newClasses = [...elementClasses.filter(filterFn), ...addClasses];

        const change = new UpdateClassesChange({
          codebaseIdToUpdateClass: element.codebaseId,
          oldClasses: elementClasses,
          newClasses,
          involvedClasses: [],
          isInstantChangeOnly: false,
        });

        useDesignCommandManager.getState().executeCommand(change);
      }
    },
    [getCurrentSelectedElements],
  );

  const applyAutoLayoutToggle = useCallback(() => {
    const elements = getCurrentSelectedElements();
    if (elements.length === 0) return;

    for (const element of elements) {
      const treeElement =
        useCodebaseStore.getState().treeElementLookup[element.codebaseId];
      if (!treeElement) continue;

      const elementClasses = treeElement.removableClasses || [];
      const hasAutoLayout =
        elementClasses.includes("flex") &&
        elementClasses.some((c) => c.startsWith("flex-"));

      if (hasAutoLayout) {
        applyClassChanges((cls) => !matchesPattern(cls, AUTO_LAYOUT_PATTERNS));
      } else {
        applyClassChanges(
          () => true,
          ["flex", "flex-row", "items-start", "gap-4"],
        );
      }
    }
  }, [getCurrentSelectedElements, applyClassChanges]);

  const applyRemoveAutoLayout = useCallback(() => {
    applyClassChanges((cls) => !matchesPattern(cls, AUTO_LAYOUT_PATTERNS));
  }, [applyClassChanges]);

  const applyRemoveFill = useCallback(() => {
    applyClassChanges((cls) => !matchesPattern(cls, FILL_PATTERNS));
  }, [applyClassChanges]);

  const applyRemoveStroke = useCallback(() => {
    applyClassChanges((cls) => !matchesPattern(cls, STROKE_PATTERNS));
  }, [applyClassChanges]);

  const applyBold = useCallback(
    () => applyClassToggle(TOGGLE_CONFIGS.bold),
    [applyClassToggle],
  );

  const applyItalic = useCallback(
    () => applyClassToggle(TOGGLE_CONFIGS.italic),
    [applyClassToggle],
  );

  const applyUnderline = useCallback(
    () => applyClassToggle(TOGGLE_CONFIGS.underline),
    [applyClassToggle],
  );

  const applyStrikethrough = useCallback(
    () => applyClassToggle(TOGGLE_CONFIGS.strikethrough),
    [applyClassToggle],
  );

  const applyAlignLeft = useCallback(
    () => applyClassSet(SET_CONFIGS.alignLeft),
    [applyClassSet],
  );

  const applyAlignCenter = useCallback(
    () => applyClassSet(SET_CONFIGS.alignCenter),
    [applyClassSet],
  );

  const applyAlignRight = useCallback(
    () => applyClassSet(SET_CONFIGS.alignRight),
    [applyClassSet],
  );

  const applyAlignJustify = useCallback(
    () => applyClassSet(SET_CONFIGS.alignJustify),
    [applyClassSet],
  );

  return {
    applyAutoLayoutToggle,
    applyRemoveAutoLayout,
    applyRemoveFill,
    applyRemoveStroke,
    applyBold,
    applyItalic,
    applyUnderline,
    applyStrikethrough,
    applyAlignLeft,
    applyAlignCenter,
    applyAlignRight,
    applyAlignJustify,
  };
};
